import { Box, Chip, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightAltOutlined as ArrowRightIcon } from "@mui/icons-material";

export default function PlanningQuickSelect(props) {
  const { plannedStart, plannedEnd, functionsRef } = props;
  const { t } = useTranslation();

  const [timeOffset, setTimeOffset] = useState(null);
  const [shortcut, setShortcut] = useState(null);

  const onReset = useCallback(() => {
    setTimeOffset(null);
    setShortcut(null);
  }, [setShortcut, setTimeOffset])

  useEffect(() => {
    if (!!functionsRef) {
      functionsRef.current = {
        reset: onReset
      };
    }
  }, [functionsRef, onReset]);


  const { dayShortcuts, weekShortcuts, timeOffsetShortcuts } = useMemo(() => {

    // Calc the day shortcuts
    const today = moment().startOf("day");

    const days = [{
      plannedStart: today,
      plannedEnd: today.clone().endOf("day"),
      label: t("mission:cards:tasks:planning:quickselect:today")
    },
    {
      plannedStart: today.clone().add(1, "days"),
      plannedEnd: today.clone().add(1, "days").endOf("day"),
      label: t("mission:cards:tasks:planning:quickselect:tomorrow")
    }];

    for (let i = 2; i < 7; i++) {
      let t = today.clone().add(i, "days");
      days.push({
        plannedStart: t,
        plannedEnd: t.clone().endOf("day"),
        label: t.format("dddd"),
      });
    }


    // Calc the week shortcuts
    const weeks = [
      {
        plannedStart: today.clone().startOf("week"),
        plannedEnd: today.clone().endOf("week"),
        label: t("mission:cards:tasks:planning:quickselect:thisWeek")
      },
      {
        plannedStart: today.clone().add(1, "week").startOf("week"),
        plannedEnd: today.clone().add(1, "week").endOf("week"),
        label: t("mission:cards:tasks:planning:quickselect:nextWeek")
      },
    ];

    const timeOffsets = [
      {
        label: t("mission:cards:tasks:planning:quickselect:during"),
        canSelectDay: true,
        canSelectWeek: true,
        startOffset: null,
        endOffset: null,
      },
      {
        label: t("mission:cards:tasks:planning:quickselect:beforeLunch"),
        canSelectDay: true,
        canSelectWeek: false,
        startOffset: 7,
        endOffset: 12,
      },
      {
        label: t("mission:cards:tasks:planning:quickselect:afterLunch"),
        canSelectDay: true,
        canSelectWeek: false,
        startOffset: 12,
        endOffset: 16,
      },
      {
        label: t("mission:cards:tasks:planning:quickselect:workingHours"),
        canSelectDay: true,
        canSelectWeek: false,
        startOffset: 8,
        endOffset: 16,
      },
      {
        label: t("mission:cards:tasks:planning:quickselect:evening"),
        canSelectDay: true,
        canSelectWeek: false,
        startOffset: 16,
        endOffset: 20,
      },
    ];

    return {
      dayShortcuts: days,
      weekShortcuts: weeks,
      timeOffsetShortcuts: timeOffsets
    }
  }, [t]);


  const onChange = useCallback((shortcut) => {
    let start = shortcut.plannedStart
    let end = shortcut.plannedEnd

    if (typeof timeOffset.startOffset === "number" && typeof timeOffset.endOffset === "number") {
      start = start.clone().startOf("day").add(timeOffset.startOffset, "hour");
      end = end.clone().startOf("day").add(timeOffset.endOffset, "hour")
    }

    plannedStart.onChange(null, start.format());
    plannedEnd.onChange(null, end.format());

    setShortcut(shortcut);
  }, [timeOffset, plannedStart.onChange, plannedEnd.onChange])


  return (
    <Box>
      <Grid container spacing={1} alignItems={"flex-start"} justifyContent={"flex-start"} >

        {
          !!timeOffset ? (
            <>
              <Grid item>
                <Chip label={timeOffset.label} variant="outlined" onDelete={onReset} />
              </Grid>

              <Grid item>
                <Box sx={{ height: "32px" }} justifyContent="center" alignItems="center" display="flex">
                  <ArrowRightIcon sx={{ color: "text.secondary" }} />
                </Box>
              </Grid>

              {
                !!shortcut ? (
                  <Grid item>
                    <Chip label={shortcut.label} variant="outlined" onDelete={() => setShortcut(null)} />
                  </Grid>
                ) : (
                  <>
                    {timeOffset.canSelectDay ? dayShortcuts.map((day, key) => (
                      <Grid item key={key}>
                        <Chip label={day.label} variant="outlined" onClick={() => onChange(day)} />
                      </Grid>
                    )) : null}

                    {timeOffset.canSelectWeek ? weekShortcuts.map((week, key) => (
                      <Grid item key={key}>
                        <Chip label={week.label} variant="outlined" onClick={() => onChange(week)} />
                      </Grid>
                    )) : null}
                  </>
                )
              }

            </>
          ) : (
            timeOffsetShortcuts.map((offs, key) => (
              <Grid item key={key}>
                <Chip label={offs.label} variant="outlined" onClick={() => setTimeOffset(offs)} />
              </Grid>
            ))
          )
        }

      </Grid>
    </Box>
  )
}